// Hook for polling job status

import { useState, useEffect, useCallback, useRef } from "react";
import type { JobResponse } from "@dishsnap/shared";
import { apiUrl } from "../config";

const POLL_INTERVAL = 1500; // 1.5 seconds

interface UseJobPollerResult {
  job: JobResponse | null;
  isPolling: boolean;
  error: string | null;
  startPolling: (jobId: string) => void;
  stopPolling: () => void;
}

export function useJobPoller(): UseJobPollerResult {
  const [job, setJob] = useState<JobResponse | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const jobIdRef = useRef<string | null>(null);

  const fetchJob = useCallback(async (jobId: string) => {
    try {
      const response = await fetch(apiUrl(`/api/jobs/${jobId}`));

      if (!response.ok) {
        const data = await response.json() as { message?: string };
        throw new Error(data.message || `HTTP ${response.status}`);
      }

      const data = await response.json() as JobResponse;
      setJob(data);
      setError(null);

      // Stop polling if job is terminal
      if (data.status === "done" || data.status === "failed") {
        setIsPolling(false);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }

      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to fetch job status";
      setError(message);
      return null;
    }
  }, []);

  const startPolling = useCallback((jobId: string) => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    jobIdRef.current = jobId;
    setIsPolling(true);
    setError(null);
    setJob(null);

    // Fetch immediately
    fetchJob(jobId);

    // Start polling
    intervalRef.current = setInterval(() => {
      if (jobIdRef.current) {
        fetchJob(jobIdRef.current);
      }
    }, POLL_INTERVAL);
  }, [fetchJob]);

  const stopPolling = useCallback(() => {
    setIsPolling(false);
    jobIdRef.current = null;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    job,
    isPolling,
    error,
    startPolling,
    stopPolling,
  };
}
